import { Identifiable, Reference } from '@approvalmax/types';
import {
    Button,
    Checkbox,
    CheckboxEditor,
    DateTimeEditor,
    FocusImplementer,
    NumberEditor,
    prioritizeAndMapDropdownItems,
    TextAreaEditor,
} from '@approvalmax/ui';
import { Box, ConditionWrapper, Flex } from '@approvalmax/ui/src/components';
import { intl, mathService } from '@approvalmax/utils';
import { useAtomValue } from 'jotai';
import sortBy from 'lodash/sortBy';
import { constants, selectors } from 'modules/common';
import { domain } from 'modules/data';
import { useSelector } from 'modules/react-redux';
import { TwoDifferentValuesWarning, WarningIndicator, WarningLevel } from 'modules/request';
import { CellIndicator, LineItemsGroupEdit, ReadOnlyCell } from 'pages/requestForm/components';
import { useGetRecognizedOcrAttachment } from 'pages/requestForm/hooks/useGetRecognizedOcrAttachment';
import { useOcrData } from 'pages/requestForm/hooks/useOcrData';
import { requestSelector } from 'pages/requestForm/selectors/pageSelectors';
import { getRequestEditMode, RequestEditMode } from 'pages/requestForm/selectors/requestSelectors';
import { FC, useCallback, useMemo } from 'react';
import { ApiTypes } from 'shared/data/webApp/v2';
import { getContactDisplayNameByIntegrationCode } from 'shared/helpers';
import { useCompanyBetaFeatures, useNetSuiteExpensesFieldSettings } from 'shared/hooks';
import { ocrIsLoadingState } from 'shared/states';

import { SectionContainer } from '../../../../components/SectionContainer';
import { NetSuiteExpenseCategory, NetSuiteSalesTaxItem } from '../../../../data/netsuite/NetSuiteContext';
import { getNetSuiteContext } from '../../../../selectors/netsuite/netSuiteSelectors';
import { ColumnDefinition } from '../../../../selectors/types/ColumnDefinition';
import { NetSuiteExpenseLineExpanded } from '../../data/types';
import { calculateLineAmountsBasedOnAmount } from '../../helpers';
import { useCurrencyPrecision, useExpenseItems, useGroupEditItems, useLineCustomFields } from '../../hooks';
import {
    checkExpensesAndItemLinesAreEmpty,
    checkLineAmortizationDateIsInvalid,
} from '../../NetSuiteRequestForm/NetSuiteRequestForm.helpers';
import {
    DropdownEditorContainer,
    GroupActions,
    SectionHeader,
    StyledLineItemsTable,
    StyledPriceCheckIndicator,
} from '../../NetSuiteRequestForm/NetSuiteRequestForm.styles';
import { netSuiteExpenseItemsState, netSuiteItemLinesState, netSuiteShowErrorsState } from '../../resources/states';
import { NetSuiteLineItemAmortizationScheduleCell } from '../NetSuiteLineItemAmortizationScheduleCell/NetSuiteLineItemAmortizationScheduleCell';
import { NetSuiteLineItemCategoryCell } from '../NetSuiteLineItemCategoryCell/NetSuiteLineItemCategoryCell';
import { NetSuiteLineItemCustomerCell } from '../NetSuiteLineItemCustomerCell/NetSuiteLineItemCustomerCell';
import { NetSuiteLineItemDropdownCell } from '../NetSuiteLineItemDropdownCell/NetSuiteLineItemDropdownCell';
import { NetSuiteLineItemProjectTaskCell } from '../NetSuiteLineItemProjectTaskCell/NetSuiteLineItemProjectTaskCell';
import { NetSuiteLineItemTaxCodeCell } from '../NetSuiteLineItemTaxCodeCell/NetSuiteLineItemTaxCodeCell';
import { getGroupEditItemsAvailableFields } from './NetSuiteExpenses.helpers';
import { messages } from './NetSuiteExpenses.messages';
import { ColumnDefinitionExtended, NetSuiteExpensesProps } from './NetSuiteExpenses.types';

const { netSuiteConstants } = constants;

const NetSuiteExpenses: FC<NetSuiteExpensesProps> = (props) => {
    const { currency, integrationCode, workflowId } = props;

    const expenseItems = useAtomValue(netSuiteExpenseItemsState);
    const itemLines = useAtomValue(netSuiteItemLinesState);
    const request = useSelector(requestSelector);

    const {
        onAddLine,
        onReorder,
        onRemoveLine,
        onCloneLine,
        onFieldUpdate,
        onMultiLineFieldUpdate,
        updateExpenseItem,
        updateCustomField,
    } = useExpenseItems(currency);

    const netSuiteEditingContext = useSelector(getNetSuiteContext);

    const ocrIsLoading = useAtomValue(ocrIsLoadingState);
    const showErrors = useAtomValue(netSuiteShowErrorsState);
    const editMode = useSelector((state) => getRequestEditMode(state, request));
    const isApproverMode = editMode === RequestEditMode.Approver;
    const ocrAttachment = useGetRecognizedOcrAttachment(request);
    const { isNetSuiteBillsItemsProjectTasks } = useCompanyBetaFeatures(request.companyId);
    const { data: ocrData } = useOcrData(request, ocrAttachment?.id);

    const sortedOcrLineItems = useMemo(
        () =>
            ocrData &&
            (sortBy(
                ocrData.document?.lineItems,
                'lineOrder'
            ) as ApiTypes['OcrContractsModelsNetSuiteRecognizedLineItemAnswerCamelCased'][]),
        [ocrData]
    );

    const currencyPrecision = useCurrencyPrecision(currency);

    const { accounts, departments, classes, locations, customFields, documentFields } = netSuiteEditingContext;

    const showErrorsOnEmptyLine = useMemo(() => {
        return checkExpensesAndItemLinesAreEmpty(expenseItems, itemLines);
    }, [expenseItems, itemLines]);

    const fieldsSettings = useNetSuiteExpensesFieldSettings(documentFields);

    const availableFields = getGroupEditItemsAvailableFields(fieldsSettings, integrationCode);

    const { availableCustomFields, customFieldsColumnDefinition } = useLineCustomFields({
        customFields,
        workflowId,
        sublistType: domain.DocumentSublistType.ExpenseList,
        showErrors,
        showErrorsOnEmptyLine,
        ocrIsLoading,
        updateCustomField,
    });

    const onChangeExpenseCategory = useCallback(
        (li: NetSuiteExpenseLineExpanded, value: NetSuiteExpenseCategory | null) => {
            if (!value) {
                updateExpenseItem(li.id, {
                    category: null,
                    account: null,
                });
            } else {
                const relatedAccount = accounts.find((account) => account.id === value.accountId);

                const categoryRate = value.rates?.find((rate) => {
                    return rate.currency === currency?.text || rate.currency === currency?.id;
                });

                const amount = typeof categoryRate?.defaultRate === 'number' ? categoryRate.defaultRate : li.amount;

                const taxAmount =
                    typeof amount === 'number' && typeof li.taxRate === 'number'
                        ? mathService.round(mathService.multiply(amount, li.taxRate / 100), 2)
                        : null;

                updateExpenseItem(li.id, {
                    category: value,
                    account: relatedAccount || null,
                    amount,
                    taxAmount,
                    rates: value.rates,
                });
            }
        },
        [accounts, currency?.id, currency?.text, updateExpenseItem]
    );

    const {
        isGroupCloneVisible,
        isGroupEditPopupVisible,
        isGroupEditVisible,
        isGroupRemoveVisible,
        isShowGroupButtons,
        groupItems,
        openGroupEdit,
        handleGroupClone,
        handleGroupRemove,
        closeGroupEdit,
    } = useGroupEditItems({
        accounts,
        departments,
        classes,
        locations,
        availableFields,
        availableCustomFields,
        workflowId,
        items: expenseItems,
        onChangeExpenseCategory,
        onCloneLine,
        onFieldUpdate,
        onMultiLineFieldUpdate,
        onRemoveLine,
        updateCustomField,
    });

    const tableDef = useMemo<ColumnDefinition<NetSuiteExpenseLineExpanded>[]>(() => {
        const definition: ColumnDefinitionExtended[] = [
            ...(isApproverMode
                ? []
                : [
                      {
                          id: 'checkbox',
                          name: '',
                          fixedWidth: 40,
                          headerCell: (lineItems) => {
                              const checked = lineItems.every((li) => li.checked);
                              const triState = lineItems.some((li) => li.checked);

                              const handleClick = () => {
                                  const nextState = !checked;

                                  onMultiLineFieldUpdate('checked', nextState);
                              };

                              return (
                                  <div onClick={handleClick}>
                                      <Checkbox size='size-18' triState={triState} checked={checked} />
                                  </div>
                              );
                          },
                          cell: (lineItem) => {
                              return (
                                  <div
                                      onClick={() => {
                                          onFieldUpdate(lineItem.id, 'checked', !lineItem.checked);
                                      }}
                                  >
                                      <Checkbox checked={lineItem.checked} size='size-18' />
                                  </div>
                              );
                          },
                      } as ColumnDefinition<NetSuiteExpenseLineExpanded>,
                  ]),
            {
                id: 'category',
                name: messages.categoryColumnName,
                fixedWidth: 274,
                isHidden: fieldsSettings.category === domain.DocumentFieldState.Hidden,
                readonly: () => fieldsSettings.category === domain.DocumentFieldState.ReadOnly || ocrIsLoading,
                invalid: (li) => {
                    if (!showErrors) {
                        return false;
                    }

                    if (
                        fieldsSettings.category === domain.DocumentFieldState.Mandatory &&
                        !li.category &&
                        !li.isEmpty
                    ) {
                        return true;
                    }

                    return showErrorsOnEmptyLine;
                },
                cell: (li, { invalid, readonly }, lineIndex) => {
                    let showMultipleCategoriesWarning = false;

                    const categoryIdsLength = (sortedOcrLineItems?.[lineIndex]?.categoryIds || []).length;

                    if (sortedOcrLineItems && categoryIdsLength) {
                        showMultipleCategoriesWarning = categoryIdsLength > 1;
                    }

                    const warningLevel = showMultipleCategoriesWarning ? WarningLevel.Yellow : WarningLevel.None;

                    return (
                        <WarningIndicator
                            show={showMultipleCategoriesWarning}
                            content={messages.multipleCategoriesWarning({
                                contactDisplayName: getContactDisplayNameByIntegrationCode(integrationCode),
                                requestName: selectors.template
                                    .getTemplateDisplayNameByCode(integrationCode, true)
                                    ?.toLowerCase(),
                            })}
                        >
                            {readonly ? (
                                <>
                                    <ReadOnlyCell text={li.category?.text ?? ''} />

                                    <CellIndicator $warningLevel={warningLevel} />
                                </>
                            ) : (
                                <DropdownEditorContainer>
                                    <NetSuiteLineItemCategoryCell
                                        invalid={invalid}
                                        value={li.category || null}
                                        singleLinePerItem={false}
                                        onChange={(value: NetSuiteExpenseCategory | null) =>
                                            onChangeExpenseCategory(li, value)
                                        }
                                        placeholder={messages.categoryFieldPlaceholder}
                                    />

                                    <CellIndicator $warningLevel={warningLevel} />
                                </DropdownEditorContainer>
                            )}
                        </WarningIndicator>
                    );
                },
            },
            {
                id: 'account',
                name: messages.accountColumnName,
                fixedWidth: 218,
                readonly: (li) => {
                    return Boolean(li.category) || ocrIsLoading;
                },
                invalid: (li) =>
                    (showErrors && !li.isEmpty && !li.account && !li.category) || (showErrors && showErrorsOnEmptyLine),
                cell: (li, { invalid, readonly }, lineIndex) => {
                    let showMultipleAccountsWarning = false;
                    let items: Reference[] | Identifiable[] = accounts;

                    if (sortedOcrLineItems) {
                        items = prioritizeAndMapDropdownItems(items, sortedOcrLineItems?.[lineIndex]?.accountIds);
                        showMultipleAccountsWarning = (sortedOcrLineItems?.[lineIndex]?.accountIds || []).length > 1;
                    }

                    const warningLevel = showMultipleAccountsWarning ? WarningLevel.Yellow : WarningLevel.None;

                    return (
                        <WarningIndicator
                            show={showMultipleAccountsWarning}
                            content={messages.multipleAccountsWarning({
                                contactDisplayName: getContactDisplayNameByIntegrationCode(integrationCode),
                                requestName: selectors.template
                                    .getTemplateDisplayNameByCode(integrationCode, true)
                                    ?.toLowerCase(),
                            })}
                        >
                            {readonly ? (
                                <>
                                    <ReadOnlyCell text={li.account?.text ?? ''} />

                                    <CellIndicator $warningLevel={warningLevel} />
                                </>
                            ) : (
                                <DropdownEditorContainer>
                                    <NetSuiteLineItemDropdownCell
                                        items={items}
                                        invalid={invalid}
                                        value={li.account || null}
                                        singleLinePerItem={false}
                                        onChange={(value: Reference | null) => {
                                            onFieldUpdate(li.id, 'account', value);
                                        }}
                                        placeholder={messages.accountFieldPlaceholder}
                                    />

                                    <CellIndicator $warningLevel={warningLevel} />
                                </DropdownEditorContainer>
                            )}
                        </WarningIndicator>
                    );
                },
            },
            {
                id: 'amount',
                name: messages.amountColumnName,
                alignRight: true,
                fixedWidth: 160,
                readonly: () => ocrIsLoading,
                invalid: (li) =>
                    (showErrors && !li.isEmpty && typeof li.amount !== 'number') ||
                    (showErrors && showErrorsOnEmptyLine),
                cell: (li, { invalid, readonly }, lineIndex) => {
                    const categoryRate = li.rates?.find((rate) => {
                        return rate.currency === currency?.text || rate.currency === currency?.id;
                    });

                    let showDifferentAmountWarning = false;

                    const amount = sortedOcrLineItems?.[lineIndex]?.amount;

                    const recognizedAmount = typeof amount === 'number' ? amount : undefined;

                    if (recognizedAmount && li.amount) {
                        showDifferentAmountWarning = li.amount !== recognizedAmount;
                    }

                    const warningLevel = showDifferentAmountWarning ? WarningLevel.Yellow : WarningLevel.None;

                    return readonly ? (
                        <WarningIndicator
                            show={showDifferentAmountWarning}
                            content={
                                <TwoDifferentValuesWarning
                                    value1={li.amount}
                                    value2={recognizedAmount}
                                    label1={messages.differentAmountWarningLabel1}
                                    label2={messages.differentAmountWarningLabel2}
                                    description={messages.differentAmountWarningDescription}
                                />
                            }
                        >
                            <>
                                <ReadOnlyCell
                                    text={li.amount !== null ? intl.formatNumber(li.amount, currencyPrecision) : ''}
                                />

                                <CellIndicator $warningLevel={warningLevel} />
                            </>
                        </WarningIndicator>
                    ) : (
                        <ConditionWrapper
                            condition={showDifferentAmountWarning}
                            wrapperIf={(children) => (
                                <WarningIndicator
                                    show={true}
                                    content={
                                        <TwoDifferentValuesWarning
                                            value1={li.amount}
                                            value2={recognizedAmount}
                                            label1={messages.differentAmountWarningLabel1}
                                            label2={messages.differentAmountWarningLabel2}
                                            description={messages.differentAmountWarningDescription}
                                        />
                                    }
                                >
                                    <>
                                        {children}

                                        <CellIndicator $warningLevel={warningLevel} />
                                    </>
                                </WarningIndicator>
                            )}
                            wrapperElse={(children) => (
                                <StyledPriceCheckIndicator
                                    actualPrice={typeof li.amount === 'number' ? li.amount : undefined}
                                    expectedPrice={
                                        typeof categoryRate?.defaultRate === 'number' && categoryRate.defaultRate !== 0
                                            ? categoryRate.defaultRate
                                            : undefined
                                    }
                                    disabled={false}
                                    integrationType={domain.IntegrationType.NetSuite}
                                    integrationCode={integrationCode}
                                >
                                    {({ warningLevel }) => (
                                        <>
                                            {children}

                                            <CellIndicator $warningLevel={warningLevel} />
                                        </>
                                    )}
                                </StyledPriceCheckIndicator>
                            )}
                        >
                            <FocusImplementer>
                                {({ registerFocusable }) => (
                                    <NumberEditor
                                        ref={registerFocusable}
                                        allowFloat
                                        invalid={invalid}
                                        value={li.amount}
                                        onChange={(newValue: number | null) => {
                                            onFieldUpdate(li.id, 'amount', newValue);
                                        }}
                                        placeholder={messages.amountFieldPlaceholder}
                                        theme='transparent'
                                        align='right'
                                        max={netSuiteConstants.maxTotalAmount}
                                        min={-netSuiteConstants.maxTotalAmount}
                                        precision={currencyPrecision}
                                        displayFormat='currency'
                                        changeOnBlur
                                    />
                                )}
                            </FocusImplementer>
                        </ConditionWrapper>
                    );
                },
            },
            {
                id: 'taxCode',
                name: messages.taxCodeColumnName,
                fixedWidth: 160,
                isHidden: fieldsSettings.taxCode === domain.DocumentFieldState.Hidden,
                readonly: () => fieldsSettings.taxCode === domain.DocumentFieldState.ReadOnly || ocrIsLoading,
                invalid: (li) =>
                    showErrors &&
                    (showErrorsOnEmptyLine ||
                        (fieldsSettings.taxCode === domain.DocumentFieldState.Mandatory && !li.taxCode && !li.isEmpty)),
                cell: (li, { invalid, readonly }, lineIndex) => {
                    let showMultipleTaxCodesWarning = false;

                    if (sortedOcrLineItems) {
                        showMultipleTaxCodesWarning = (sortedOcrLineItems?.[lineIndex]?.taxCodeIds || []).length > 1;
                    }

                    const warningLevel = showMultipleTaxCodesWarning ? WarningLevel.Yellow : WarningLevel.None;

                    return (
                        <WarningIndicator
                            show={showMultipleTaxCodesWarning}
                            content={messages.multipleTaxCodesWarning({
                                contactDisplayName: getContactDisplayNameByIntegrationCode(integrationCode),
                                requestName: selectors.template
                                    .getTemplateDisplayNameByCode(integrationCode, true)
                                    ?.toLowerCase(),
                            })}
                        >
                            {readonly ? (
                                <>
                                    <ReadOnlyCell text={li.taxCode?.text ?? ''} />

                                    <CellIndicator $warningLevel={warningLevel} />
                                </>
                            ) : (
                                <DropdownEditorContainer>
                                    <NetSuiteLineItemTaxCodeCell
                                        lineIndex={lineIndex}
                                        invalid={invalid}
                                        value={li.taxCode || null}
                                        singleLinePerItem={false}
                                        onChange={(value: NetSuiteSalesTaxItem | null) => {
                                            updateExpenseItem(li.id, {
                                                taxCode: value,
                                                taxRate: value?.rate ?? null,
                                                ...calculateLineAmountsBasedOnAmount(
                                                    li.amount,
                                                    value?.rate ?? null,
                                                    currencyPrecision
                                                ),
                                            });
                                        }}
                                        placeholder={messages.taxCodeFieldPlaceholder}
                                    />

                                    <CellIndicator $warningLevel={warningLevel} />
                                </DropdownEditorContainer>
                            )}
                        </WarningIndicator>
                    );
                },
            },
            {
                id: 'taxRate',
                name: messages.taxRateColumnName,
                alignRight: true,
                fixedWidth: 160,
                readonly: () => true,
                invalid: () => showErrors && showErrorsOnEmptyLine,
                isHidden: fieldsSettings.taxRate === domain.DocumentFieldState.Hidden,
                cell: (li) => <ReadOnlyCell text={typeof li.taxRate === 'number' ? `${li.taxRate}%` : ''} />,
            },
            {
                id: 'taxAmount',
                name: messages.taxAmountColumnName,
                alignRight: true,
                fixedWidth: 160,
                readonly: () => true,
                invalid: (li) => {
                    if (typeof li.taxAmount === 'number') {
                        let isInvalidTaxAmount = false;

                        if (
                            li.taxAmount > netSuiteConstants.taxAmountMaxValue ||
                            li.taxAmount < -netSuiteConstants.taxAmountMaxValue
                        ) {
                            isInvalidTaxAmount = true;
                        } else {
                            const taxAmountDigitsCount = li.taxAmount.toString().replace(/[^\d]/g, '').length;

                            if (taxAmountDigitsCount > netSuiteConstants.taxAmountMaxDigitsCount) {
                                isInvalidTaxAmount = true;
                            }
                        }

                        return showErrors && isInvalidTaxAmount;
                    }

                    return showErrors && showErrorsOnEmptyLine;
                },
                isHidden: fieldsSettings.taxAmount === domain.DocumentFieldState.Hidden,
                cell: (li, _, lineIndex) => {
                    const value = li.taxAmount !== null ? li.taxAmount : null;

                    let showDifferentTaxAmountWarning = false;

                    // TODO: Remove when backend is ready.
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-expect-error
                    const recognizedTaxAmount = sortedOcrLineItems?.[lineIndex]?.taxAmount;

                    if (value && recognizedTaxAmount) {
                        showDifferentTaxAmountWarning = value !== recognizedTaxAmount;
                    }

                    const warningLevel = showDifferentTaxAmountWarning ? WarningLevel.Yellow : WarningLevel.None;

                    return (
                        <WarningIndicator
                            show={showDifferentTaxAmountWarning}
                            content={
                                <TwoDifferentValuesWarning
                                    value1={value}
                                    value2={recognizedTaxAmount}
                                    label1={messages.differentTaxAmountWarningLabel1}
                                    label2={messages.differentTaxAmountWarningLabel2}
                                    description={messages.differentTaxAmountWarningDescription}
                                />
                            }
                        >
                            <ReadOnlyCell
                                text={li.taxAmount !== null ? intl.formatNumber(li.taxAmount, currencyPrecision) : ''}
                            />

                            <CellIndicator $warningLevel={warningLevel} />
                        </WarningIndicator>
                    );
                },
            },
            {
                id: 'grossAmount',
                name: messages.grossAmountColumnName,
                alignRight: true,
                fixedWidth: 160,
                invalid: () => showErrors && showErrorsOnEmptyLine,
                readonly: () => ocrIsLoading,
                cell: (li, { invalid, readonly }) =>
                    readonly ? (
                        <ReadOnlyCell
                            text={li.grossAmount !== null ? intl.formatNumber(li.grossAmount, currencyPrecision) : ''}
                        />
                    ) : (
                        <FocusImplementer>
                            {({ registerFocusable }) => (
                                <NumberEditor
                                    ref={registerFocusable}
                                    allowFloat
                                    invalid={invalid}
                                    value={li.grossAmount}
                                    onChange={(newValue: number | null) => {
                                        onFieldUpdate(li.id, 'grossAmount', newValue);
                                    }}
                                    placeholder={messages.grossAmountFieldPlaceholder}
                                    theme='transparent'
                                    align='right'
                                    max={netSuiteConstants.maxTotalAmount}
                                    min={-netSuiteConstants.maxTotalAmount}
                                    precision={currencyPrecision}
                                    displayFormat='currency'
                                    changeOnBlur
                                />
                            )}
                        </FocusImplementer>
                    ),
            },
            {
                id: 'memo',
                name: messages.memoColumnName,
                fixedWidth: 274,
                isHidden: fieldsSettings.memo === domain.DocumentFieldState.Hidden,
                readonly: () => fieldsSettings.memo === domain.DocumentFieldState.ReadOnly || ocrIsLoading,
                invalid: (li) => {
                    if (!showErrors) {
                        return false;
                    }

                    if (fieldsSettings.memo === domain.DocumentFieldState.Mandatory && !li.memo && !li.isEmpty) {
                        return true;
                    }

                    return showErrorsOnEmptyLine;
                },
                cell: (li, { invalid, readonly }) =>
                    readonly ? (
                        <ReadOnlyCell text={li.memo || ''} multiline />
                    ) : (
                        <FocusImplementer>
                            {({ registerFocusable }) => (
                                <TextAreaEditor
                                    ref={registerFocusable}
                                    invalid={invalid}
                                    value={li.memo || ''}
                                    onChange={(value) => {
                                        onFieldUpdate(li.id, 'memo', value);
                                    }}
                                    placeholder={messages.memoFieldPlaceholder}
                                    theme='transparent'
                                    maxLength={netSuiteConstants.memoMaxLength}
                                    changeOnBlur
                                />
                            )}
                        </FocusImplementer>
                    ),
            },
            {
                id: 'department',
                name: messages.departmentColumnName,
                fixedWidth: 274,
                isHidden: fieldsSettings.department === domain.DocumentFieldState.Hidden,
                readonly: () => fieldsSettings.department === domain.DocumentFieldState.ReadOnly || ocrIsLoading,
                invalid: (li) => {
                    if (!showErrors) {
                        return false;
                    }

                    if (
                        fieldsSettings.department === domain.DocumentFieldState.Mandatory &&
                        !li.department &&
                        !li.isEmpty
                    ) {
                        return true;
                    }

                    return showErrorsOnEmptyLine;
                },
                cell: (li, { invalid, readonly }) =>
                    readonly ? (
                        <ReadOnlyCell text={li.department?.text ?? ''} />
                    ) : (
                        <DropdownEditorContainer>
                            <NetSuiteLineItemDropdownCell
                                items={departments}
                                invalid={invalid}
                                value={li.department || null}
                                singleLinePerItem={false}
                                onChange={(value: Reference | null) => {
                                    onFieldUpdate(li.id, 'department', value);
                                }}
                                placeholder={messages.departmentFieldPlaceholder}
                            />
                        </DropdownEditorContainer>
                    ),
            },
            {
                id: 'class',
                name: messages.classColumnName,
                fixedWidth: 180,
                isHidden: fieldsSettings.class === domain.DocumentFieldState.Hidden,
                readonly: () => fieldsSettings.class === domain.DocumentFieldState.ReadOnly || ocrIsLoading,
                invalid: (li) => {
                    if (!showErrors) {
                        return false;
                    }

                    if (fieldsSettings.class === domain.DocumentFieldState.Mandatory && !li.class && !li.isEmpty) {
                        return true;
                    }

                    return showErrorsOnEmptyLine;
                },
                cell: (li, { invalid, readonly }) =>
                    readonly ? (
                        <ReadOnlyCell text={li.class?.text ?? ''} />
                    ) : (
                        <DropdownEditorContainer>
                            <NetSuiteLineItemDropdownCell
                                items={classes}
                                invalid={invalid}
                                value={li.class || null}
                                singleLinePerItem={false}
                                onChange={(value: Reference | null) => {
                                    onFieldUpdate(li.id, 'class', value);
                                }}
                                placeholder={messages.classFieldPlaceholder}
                            />
                        </DropdownEditorContainer>
                    ),
            },
            {
                id: 'location',
                name: messages.locationColumnName,
                fixedWidth: 180,
                isHidden: fieldsSettings.location === domain.DocumentFieldState.Hidden,
                readonly: () => fieldsSettings.location === domain.DocumentFieldState.ReadOnly || ocrIsLoading,
                invalid: (li) => {
                    if (!showErrors) {
                        return false;
                    }

                    if (
                        fieldsSettings.location === domain.DocumentFieldState.Mandatory &&
                        !li.location &&
                        !li.isEmpty
                    ) {
                        return true;
                    }

                    return showErrorsOnEmptyLine;
                },
                cell: (li, { invalid, readonly }) =>
                    readonly ? (
                        <ReadOnlyCell text={li.location?.text ?? ''} />
                    ) : (
                        <DropdownEditorContainer>
                            <NetSuiteLineItemDropdownCell
                                items={locations}
                                invalid={invalid}
                                value={li.location || null}
                                singleLinePerItem={false}
                                onChange={(value: Reference | null) => {
                                    onFieldUpdate(li.id, 'location', value);
                                }}
                                placeholder={messages.locationFieldPlaceholder}
                            />
                        </DropdownEditorContainer>
                    ),
            },
            {
                id: 'customer',
                name: messages.customerColumnName,
                fixedWidth: 218,
                isHidden: fieldsSettings.customer === domain.DocumentFieldState.Hidden,
                readonly: () => fieldsSettings.customer === domain.DocumentFieldState.ReadOnly || ocrIsLoading,
                invalid: (li) => {
                    if (!showErrors) {
                        return false;
                    }

                    if (
                        fieldsSettings.customer === domain.DocumentFieldState.Mandatory &&
                        !li.customer &&
                        !li.isEmpty
                    ) {
                        return true;
                    }

                    return showErrorsOnEmptyLine;
                },
                cell: (li, { invalid, readonly }, lineIndex) => {
                    let showMultipleCustomersWarning = false;

                    // TODO: Can't find `customerIds` in the api scheme. Sync with the backend team.
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-expect-error
                    const customerIdsLength = (sortedOcrLineItems?.[lineIndex]?.customerIds || []).length;

                    if (sortedOcrLineItems && customerIdsLength) {
                        showMultipleCustomersWarning = customerIdsLength > 1;
                    }

                    const warningLevel = showMultipleCustomersWarning ? WarningLevel.Yellow : WarningLevel.None;

                    return (
                        <WarningIndicator
                            show={showMultipleCustomersWarning}
                            content={messages.multipleCustomersWarning({
                                contactDisplayName: getContactDisplayNameByIntegrationCode(integrationCode),
                                requestName: selectors.template
                                    .getTemplateDisplayNameByCode(integrationCode, true)
                                    ?.toLowerCase(),
                            })}
                        >
                            {readonly ? (
                                <>
                                    <ReadOnlyCell text={li.customer?.text ?? ''} />

                                    <CellIndicator $warningLevel={warningLevel} />
                                </>
                            ) : (
                                <DropdownEditorContainer>
                                    <NetSuiteLineItemCustomerCell
                                        invalid={invalid}
                                        value={li.customer || null}
                                        singleLinePerItem={false}
                                        onChange={(value) => {
                                            onFieldUpdate(li.id, 'customer', value);
                                        }}
                                        placeholder={messages.customerFieldPlaceholder}
                                    />

                                    <CellIndicator $warningLevel={warningLevel} />
                                </DropdownEditorContainer>
                            )}
                        </WarningIndicator>
                    );
                },
            },
            ...(integrationCode === domain.IntegrationCode.NetSuiteBill && isNetSuiteBillsItemsProjectTasks
                ? ([
                      {
                          id: 'projectTask',
                          name: messages.projectTaskColumnName,
                          fixedWidth: 218,
                          isHidden: fieldsSettings.projectTask === domain.DocumentFieldState.Hidden,
                          readonly: (li) =>
                              !li.customer ||
                              fieldsSettings.projectTask === domain.DocumentFieldState.ReadOnly ||
                              ocrIsLoading,
                          invalid: (li) => {
                              if (!showErrors) {
                                  return false;
                              }

                              if (
                                  fieldsSettings.projectTask === domain.DocumentFieldState.Mandatory &&
                                  !li.projectTask &&
                                  !li.isEmpty
                              ) {
                                  return true;
                              }

                              return showErrorsOnEmptyLine;
                          },
                          cell: (li, { invalid, readonly }) => {
                              return readonly ? (
                                  <ReadOnlyCell text={li.projectTask?.text ?? ''} />
                              ) : (
                                  <DropdownEditorContainer>
                                      <NetSuiteLineItemProjectTaskCell
                                          invalid={invalid}
                                          value={li.projectTask || null}
                                          singleLinePerItem={false}
                                          onChange={(value) => {
                                              onFieldUpdate(li.id, 'projectTask', value);
                                          }}
                                          placeholder={messages.projectTaskFieldPlaceholder}
                                          customerId={li.customer?.id}
                                      />
                                  </DropdownEditorContainer>
                              );
                          },
                      },
                  ] as ColumnDefinitionExtended[])
                : []),
            {
                id: 'billable',
                name: messages.billableColumnName,
                fixedWidth: 80,
                isHidden: fieldsSettings.isBillable === domain.DocumentFieldState.Hidden,
                readonly: (li) =>
                    !li.customer || fieldsSettings.isBillable === domain.DocumentFieldState.ReadOnly || ocrIsLoading,
                invalid: () => showErrors && showErrorsOnEmptyLine,
                cell: (li, { readonly }) => (
                    <Flex alignItems='center'>
                        <CheckboxEditor
                            disabled={readonly}
                            value={li.customer ? Boolean(li.isBillable) : false}
                            onChange={(value) => {
                                onFieldUpdate(li.id, 'isBillable', value);
                            }}
                        />
                    </Flex>
                ),
            },
            ...(integrationCode === domain.IntegrationCode.NetSuiteBill
                ? ([
                      {
                          id: 'amortizationSchedule',
                          name: messages.amortizationSchedule,
                          fixedWidth: 218,
                          isHidden: fieldsSettings.amortizationSchedule === domain.DocumentFieldState.Hidden,
                          readonly: () =>
                              fieldsSettings.amortizationSchedule === domain.DocumentFieldState.ReadOnly ||
                              ocrIsLoading,
                          invalid: (li) => {
                              if (!showErrors) {
                                  return false;
                              }

                              if (
                                  fieldsSettings.amortizationSchedule === domain.DocumentFieldState.Mandatory &&
                                  !li.amortizationSchedule &&
                                  !li.isEmpty
                              ) {
                                  return true;
                              }

                              return showErrorsOnEmptyLine;
                          },
                          cell: (li, { invalid, readonly }) =>
                              readonly ? (
                                  <ReadOnlyCell text={li.amortizationSchedule?.text ?? ''} />
                              ) : (
                                  <DropdownEditorContainer>
                                      <NetSuiteLineItemAmortizationScheduleCell
                                          invalid={invalid}
                                          value={li.amortizationSchedule || null}
                                          singleLinePerItem={false}
                                          onChange={(value) => {
                                              onFieldUpdate(li.id, 'amortizationSchedule', value);
                                          }}
                                          placeholder={messages.customerFieldPlaceholder}
                                      />
                                  </DropdownEditorContainer>
                              ),
                      },
                      {
                          id: 'amortizationStartDate',
                          name: messages.amortizationStartDate,
                          fixedWidth: 218,
                          isHidden: fieldsSettings.amortizationStartDate === domain.DocumentFieldState.Hidden,
                          readonly: () =>
                              fieldsSettings.amortizationStartDate === domain.DocumentFieldState.ReadOnly ||
                              ocrIsLoading,
                          invalid: (li) => {
                              if (!showErrors) {
                                  return false;
                              }

                              if (checkLineAmortizationDateIsInvalid(li)) {
                                  return true;
                              }

                              if (
                                  fieldsSettings.amortizationStartDate === domain.DocumentFieldState.Mandatory &&
                                  !li.amortizationStartDate &&
                                  !li.isEmpty
                              ) {
                                  return true;
                              }

                              return showErrorsOnEmptyLine;
                          },
                          cell: (li, { invalid, readonly }) =>
                              readonly ? (
                                  <ReadOnlyCell text={li.amortizationStartDate ?? ''} />
                              ) : (
                                  <FocusImplementer>
                                      {({ registerFocusable }) => (
                                          <DateTimeEditor
                                              ref={registerFocusable}
                                              value={li.amortizationStartDate || null}
                                              placeholder={messages.datePlaceholder}
                                              onChange={(value) => onFieldUpdate(li.id, 'amortizationStartDate', value)}
                                              theme='transparent'
                                              invalid={invalid}
                                          />
                                      )}
                                  </FocusImplementer>
                              ),
                      },
                      {
                          id: 'amortizationEndDate',
                          name: messages.amortizationEndDate,
                          fixedWidth: 218,
                          isHidden: fieldsSettings.amortizationEndDate === domain.DocumentFieldState.Hidden,
                          readonly: () =>
                              fieldsSettings.amortizationEndDate === domain.DocumentFieldState.ReadOnly || ocrIsLoading,
                          invalid: (li) => {
                              if (!showErrors) {
                                  return false;
                              }

                              if (checkLineAmortizationDateIsInvalid(li)) {
                                  return true;
                              }

                              if (
                                  fieldsSettings.amortizationEndDate === domain.DocumentFieldState.Mandatory &&
                                  !li.amortizationEndDate &&
                                  !li.isEmpty
                              ) {
                                  return true;
                              }

                              return showErrorsOnEmptyLine;
                          },
                          cell: (li, { invalid, readonly }) =>
                              readonly ? (
                                  <ReadOnlyCell text={li.amortizationStartDate ?? ''} />
                              ) : (
                                  <FocusImplementer>
                                      {({ registerFocusable }) => (
                                          <DateTimeEditor
                                              ref={registerFocusable}
                                              value={li.amortizationEndDate || null}
                                              placeholder={messages.datePlaceholder}
                                              onChange={(value) => onFieldUpdate(li.id, 'amortizationEndDate', value)}
                                              theme='transparent'
                                              invalid={invalid}
                                          />
                                      )}
                                  </FocusImplementer>
                              ),
                      },
                      {
                          id: 'amortizationResidual',
                          name: messages.amortizationResidual,
                          alignRight: true,
                          fixedWidth: 160,
                          isHidden: fieldsSettings.amortizationResidual === domain.DocumentFieldState.Hidden,
                          readonly: () =>
                              fieldsSettings.amortizationResidual === domain.DocumentFieldState.ReadOnly ||
                              ocrIsLoading,
                          invalid: (li) => {
                              if (!showErrors) {
                                  return false;
                              }

                              if (
                                  fieldsSettings.amortizationResidual === domain.DocumentFieldState.Mandatory &&
                                  typeof li.amortizationResidual !== 'number' &&
                                  !li.isEmpty
                              ) {
                                  return true;
                              }

                              return showErrorsOnEmptyLine;
                          },
                          cell: (li, { invalid, readonly }) => {
                              return readonly ? (
                                  <ReadOnlyCell
                                      text={
                                          typeof li.amortizationResidual === 'number'
                                              ? intl.formatNumber(li.amortizationResidual, currencyPrecision)
                                              : ''
                                      }
                                  />
                              ) : (
                                  <FocusImplementer>
                                      {({ registerFocusable }) => (
                                          <>
                                              <NumberEditor
                                                  ref={registerFocusable}
                                                  allowFloat
                                                  invalid={invalid}
                                                  value={
                                                      typeof li.amortizationResidual === 'number'
                                                          ? li.amortizationResidual
                                                          : null
                                                  }
                                                  onChange={(newValue: number | null) => {
                                                      onFieldUpdate(li.id, 'amortizationResidual', newValue);
                                                  }}
                                                  placeholder={messages.inputPlaceholder}
                                                  theme='transparent'
                                                  align='right'
                                                  max={netSuiteConstants.maxTotalAmount}
                                                  min={-netSuiteConstants.maxTotalAmount}
                                                  precision={currencyPrecision}
                                                  displayFormat='currency'
                                                  changeOnBlur
                                              />
                                          </>
                                      )}
                                  </FocusImplementer>
                              );
                          },
                      },
                  ] as ColumnDefinitionExtended[])
                : []),
            ...customFieldsColumnDefinition,
        ];

        return definition.filter((col) => !col.isHidden);
    }, [
        isApproverMode,
        fieldsSettings.category,
        fieldsSettings.taxCode,
        fieldsSettings.taxRate,
        fieldsSettings.taxAmount,
        fieldsSettings.memo,
        fieldsSettings.department,
        fieldsSettings.class,
        fieldsSettings.location,
        fieldsSettings.customer,
        fieldsSettings.projectTask,
        fieldsSettings.isBillable,
        fieldsSettings.amortizationSchedule,
        fieldsSettings.amortizationStartDate,
        fieldsSettings.amortizationEndDate,
        fieldsSettings.amortizationResidual,
        integrationCode,
        isNetSuiteBillsItemsProjectTasks,
        customFieldsColumnDefinition,
        onMultiLineFieldUpdate,
        onFieldUpdate,
        ocrIsLoading,
        showErrors,
        showErrorsOnEmptyLine,
        sortedOcrLineItems,
        onChangeExpenseCategory,
        accounts,
        currencyPrecision,
        currency?.text,
        currency?.id,
        updateExpenseItem,
        departments,
        classes,
        locations,
    ]);

    return (
        <SectionContainer
            title={
                <SectionHeader>
                    <Box spacing='0 20'>{messages.sectionName}</Box>

                    {isShowGroupButtons && (
                        <GroupActions>
                            {isGroupEditVisible && (
                                <Button preset='small' execute={openGroupEdit}>
                                    {messages.groupEdit}
                                </Button>
                            )}

                            {isGroupCloneVisible && (
                                <Button preset='small' execute={handleGroupClone}>
                                    {messages.groupClone}
                                </Button>
                            )}

                            {isGroupRemoveVisible && (
                                <Button preset='small' execute={handleGroupRemove}>
                                    {messages.groupRemove}
                                </Button>
                            )}
                        </GroupActions>
                    )}
                </SectionHeader>
            }
            noPadding
        >
            <StyledLineItemsTable
                columnDefinitions={tableDef}
                lineItems={expenseItems}
                invalid={false}
                onReorder={onReorder}
                onRemoveLine={onRemoveLine}
                onCloneLine={onCloneLine}
                onAddLine={onAddLine}
                canAddLine={true}
            />

            {isGroupEditPopupVisible && <LineItemsGroupEdit onClose={closeGroupEdit} items={groupItems} />}
        </SectionContainer>
    );
};

export default NetSuiteExpenses;
